import { firstNonEmpty } from "./firstNonEmpty"

export interface ResolvedAlt {
  /** Final alt attribute value. Empty string when the image is decorative. */
  alt: string
  /** True when the CMS marked the image decorative (alt explicitly blank). */
  decorative: boolean
}

/**
 * Resolves an image's alt text with decorative-image support (WCAG 1.1.1).
 *
 * An *explicitly* blank CMS alt (empty/whitespace string, distinct from
 * `null`/`undefined`, which mean "no alt authored") marks the image decorative:
 * callers render `alt=""` + `aria-hidden="true"` so assistive tech ignores it.
 * Otherwise the trimmed alt is used, falling back to the first non-empty value.
 */
export const resolveDecorativeAlt = (
  rawAlt: string | null | undefined,
  fallbacks: ReadonlyArray<string | null | undefined>,
  defaultAlt: string
): ResolvedAlt => {
  if (typeof rawAlt === "string") {
    const trimmed = rawAlt.trim()
    if (trimmed.length === 0) {
      return { alt: "", decorative: true }
    }
    return { alt: trimmed, decorative: false }
  }
  return { alt: firstNonEmpty(fallbacks, defaultAlt), decorative: false }
}
